import * as SelectPrimitive from "@radix-ui/react-select";
import { Plus, Trash2 } from "lucide-react";
import { ComponentProps, useEffect, useState } from "react";
import t from "@/lang/lang";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { TextInput } from "@/shared/components/form/text-input";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useTextblocksCatalogListQuery,
} from "@/services/backend/textblocks/api/catalog-api";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import {
  useTextblocksDefaultCatalogDeleteMutation,
  useTextblocksDefaultCatalogListQuery,
  useTextblocksDefaultCatalogPutMutation,
} from "@/services/backend/textblocks/api/default-catalog-api";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { CatalogName } from "@/services/backend/textblocks/components/catalog-name";
import { Label } from "@/shared/components/ui/label";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

export function DefaultCatalogCard() {
  const { data, isLoading, error } = useTextblocksDefaultCatalogListQuery({});

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Standard Kataloge")}</CardTitle>
        <CardDescription>
          {t(
            "Diese Kataloge werden verwendet, wenn ein neues Dokument erstellt wird.",
          )}
        </CardDescription>
        <CardAction>
          <CreateDefaultCatalogDialog />
        </CardAction>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Identifikator (z.B. Dienstleistung)")}</TableHead>
              <TableHead>{t("Katalog")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={3} />}
            {error && <TableRtkErrorRow colSpan={3} error={error} />}
            {data && data.items.length === 0 && <TableEmptyRow colSpan={3} />}
            {data &&
              data.items.map((defaultCatalog) => (
                <TableRow key={defaultCatalog.identifier}>
                  <TableCell>{defaultCatalog.identifier}</TableCell>
                  <TableCell>
                    <CatalogName catalogId={defaultCatalog.catalogId} />
                  </TableCell>
                  <TableCell className="text-right">
                    <DeleteDefaultCatalogDialog
                      identifier={defaultCatalog.identifier}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CreateDefaultCatalogDialog() {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState({ identifier: "", catalogId: "" });
  const [put, { isLoading, error, isSuccess, reset }] =
    useTextblocksDefaultCatalogPutMutation();
  const { companyId } = useActiveCompany();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
      setRequest({ identifier: "", catalogId: "" });
    }
  }, [isSuccess, reset]);

  const doCreate = () => {
    if (isLoading) return;

    put(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus />
          {t("Standard Katalog erstellen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Standard Katalog erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Der Identifikator ist typischerweise die Dienstleistung. Kann aber jeder beliebige Text sein.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TextInput
            label={t("Identifikator")}
            field="identifier"
            text={request.identifier}
            onTextChange={(identifier) =>
              setRequest({ ...request, identifier })
            }
            errors={inputValidationErrorMessages(error, "identifier")}
          />
          <div className="grid content-start gap-1.5">
            <Label>{t("Katalog")}</Label>
            <CatalogSelect
              value={request.catalogId}
              onValueChange={(catalogId) =>
                setRequest({ ...request, catalogId })
              }
              request={{ company: { active: true, values: [companyId] } }}
            />
          </div>
        </div>
        <CommonDialogFooter isLoading={isLoading} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}

function CatalogSelect({
  request,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & {
  request: ListRequest;
}) {
  const { data: catalogList } = useTextblocksCatalogListQuery(request);

  return (
    <Select {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Katalog auswählen" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {catalogList?.items.map((catalog) => (
            <SelectItem value={catalog.id} key={catalog.id}>
              {catalog.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function DeleteDefaultCatalogDialog({ identifier }: { identifier: string }) {
  const [open, setOpen] = useState(false);
  const [del, { isLoading, isSuccess, error, reset }] =
    useTextblocksDefaultCatalogDeleteMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      reset();
    }
  }, [isSuccess, reset]);

  const doDelete = () => {
    if (isLoading) return;
    del({ identifier });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Standard Katalog löschen")}</DialogTitle>
          <DialogDescription>
            {t(
              `Möchten Sie diesen Standard Katalog mit dem Identifikator "${identifier}" wirklich löschen?`,
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
